import type { ActionFunctionArgs, LoaderFunctionArgs, SerializeFrom } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

import { AppErrorBoundary } from "~/components/AppErrorBoundary";
import { StepInfo } from "~/components/Step/Step";
import { createServerStateManager } from "~/modules/StateManager.server";
import { handleSubjectAction } from "~/modules/subject/subject.action.server";
import { handleSubjectLoader } from "~/modules/subject/subject.loader.server";
import { SubjectGroupContainer } from "~/modules/subject/SubjectGroup";
import { requireStep } from "~/utils/validators.utils.server";

export async function action(args: ActionFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise_schedule_subject_action",
    async function scheduleSubjectAction() {
      const stateManager = await createServerStateManager(args);
      const { nextStep } = stateManager.getStepConfig("subject");
      requireStep(nextStep, "Subject");

      await handleSubjectAction(stateManager);

      return redirect(`../${nextStep.pathname}`);
    },
  );
}

export interface SubjectLoaderData extends SerializeFrom<typeof loader> {}

export async function loader(args: LoaderFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise_schedule_subject",
    async function scheduleSubjectLoader() {
      const stateManager = await createServerStateManager(args);
      const { nextStep } = stateManager.getStepConfig("subject");
      requireStep(nextStep, "Subject");

      const result = await handleSubjectLoader(stateManager);

      if (result.type === "redirect") {
        return redirect(`../${nextStep.pathname}`);
      }

      return json(result.payload);
    },
  );
}

export default function SubjectRoute() {
  const categories = useLoaderData<typeof loader>();

  return (
    <div data-component="SubjectStep">
      <StepInfo i18nKey="steps.Subject.Info" />
      <SubjectGroupContainer categories={categories} />
    </div>
  );
}

export function ErrorBoundary() {
  return <AppErrorBoundary />;
}
